import type { ReportStandardFundForPublicModel } from '@/features/mold/models/standard-fund';
import {
  convertToNegative,
  formatAmountReport,
  validateTotalWithElements,
} from '@/utils';
import type { z } from 'zod';

type StandardFundReportForPublicValueConverterArgs = {
  report: z.infer<typeof ReportStandardFundForPublicModel>;
  type: 'csv' | 'pdf';
  isDebugMode: boolean;
};
export const standardFundReportForPublicValueConverter = ({
  report,
  type,
  isDebugMode,
}: StandardFundReportForPublicValueConverterArgs) => {
  const { result: isNullEndingOfTheFundBalance } = validateTotalWithElements({
    totalValue: report.endingOfTheFundBalance,
    elements: [
      {
        value: report.beginningOfTheFundBalance,
        rawValue: report.rawBeginningOfTheFundBalance,
        negativeCount: 0,
      },
      {
        value: report.totalIncome,
        rawValue: report.rawTotalIncome,
        negativeCount: report.negativeTotalIncomeCount,
      },
      {
        value: convertToNegative(report.totalExpenses),
        rawValue: convertToNegative(report.rawTotalExpenses),
        negativeCount: report.negativeTotalExpensesCount,
      },
      {
        value: convertToNegative(report.amountReturnedToTheNationalTreasury),
        rawValue: convertToNegative(
          report.rawAmountReturnedToTheNationalTreasury,
        ),
        negativeCount: 0,
      },
      {
        value: convertToNegative(report.amountReturnedToTheOrganization),
        rawValue: convertToNegative(report.rawAmountReturnedToTheOrganization),
        negativeCount: 0,
      },
    ],
  });

  return {
    totalIncome: formatAmountReport(report.totalIncome, type, {
      isDebugMode,
      negativeCount: report.negativeTotalIncomeCount,
      rawValue: type === 'pdf' ? report.rawTotalIncome : undefined,
    }),
    totalExpenses: formatAmountReport(report.totalExpenses, type, {
      isDebugMode,
      negativeCount: report.negativeTotalExpensesCount,
      rawValue: type === 'pdf' ? report.rawTotalExpenses : undefined,
    }),
    endingOfTheFundBalance: formatAmountReport(
      report.endingOfTheFundBalance,
      type,
      {
        isDebugMode,
        negativeCount:
          isNullEndingOfTheFundBalance === 'isNull' ||
          report.isCanceledEndingOfTheFundBalance
            ? 1
            : 0,
        rawValue: type === 'pdf' ? report.rawEndingOfTheFundBalance : undefined,
      },
    ),
  };
};
